<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaFilterPanel from '@/components/design/composite/GaiaFilterPanel.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import GaiaClauseDetailDrawer from '@/components/design/domain/clauses/GaiaClauseDetailDrawer.vue';
import GaiaClauseFormModal, {
  type GaiaClauseFormMode,
} from '@/components/design/domain/clauses/GaiaClauseFormModal.vue';
import { clauseCategoryMap, contractTypeNames } from '@/components/design/statusMaps';
import { gaiaAccent, gaiaContentWidth, gaiaNeutral } from '@/components/design/themes/tokens';
import { useClauseFilterLabels } from '@/composables/useClauseFilterLabels';
import { useCopyToClipboard } from '@/composables/useCopyToClipboard';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { useRowDelete } from '@/composables/useRowDelete';
import { useAuthStore } from '@/stores/auth';
import {
  CLAUSE_PAGE_SIZES,
  ClauseLibraryService,
  buildColumnFilters,
  canWriteClauses,
  normalizeFilterOptions,
  type ClauseLibrary,
  type LibraryClause,
} from '@/services/clauseLibraryService';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * The clause library — approved wording, in two libraries.
 *
 * The page is one list rendered twice: the GAIA managed library and the
 * organisation's own have identical columns, so the tab decides which endpoint
 * pair and which permission apply and nothing else. `ClauseLibraryService`
 * owns that choice; this view only knows "the current library".
 *
 * Search, filters, sort, paging and the active tab are all server-side and all
 * URL-synced, so a filtered page is a shareable link.
 */

const message = useGaiaToast();
const authStore = useAuthStore();
const { copy } = useCopyToClipboard();
const { filterConfig, valueLabelMap } = useClauseFilterLabels();

const {
  activeTab,
  currentPage,
  pageSize,
  searchQuery,
  sortKey,
  sortDirection,
  sortActivated,
  columnFilters,
  setColumnFilters,
} = useListQuerySync({
  tabParam: 'library',
  defaultTab: 'organisation',
  allowedTabs: ['organisation', 'gaia'],
  defaultSortKey: 'created_at',
  defaultSortDirection: 'desc',
  allowedPageSizes: CLAUSE_PAGE_SIZES,
});

const clauses = ref<LibraryClause[]>([]);
const totalCount = ref<number>(0);
const filterOptions = ref<Record<string, string[]>>({});
const loading = ref<boolean>(false);
const loadError = ref<string | null>(null);
/** Set when a refresh fails with rows still on screen — see `fetchClauses`. */
const staleSince = ref<string | null>(null);
const showFilterPanel = ref<boolean>(false);

/**
 * Both tabs' totals, for the count pills. The active library's comes free with
 * its page; the other one is a `limit: 0` search, which returns the count
 * without a page of rows.
 */
const libraryCounts = reactive<Record<ClauseLibrary, number | null>>({
  gaia: null,
  organisation: null,
});

const rowDelete = useRowDelete<LibraryClause>();

// --- the detail drawer -----------------------------------------------------
const drawerOpen = ref<boolean>(false);
const selected = ref<LibraryClause | null>(null);

// --- the create / edit modal -----------------------------------------------
const formOpen = ref<boolean>(false);
const formMode = ref<GaiaClauseFormMode>('create');
const formLibrary = ref<ClauseLibrary>('organisation');
const formClause = ref<LibraryClause | null>(null);
const formPrefill = ref<LibraryClause | null>(null);
const formHeading = ref<string | undefined>(undefined);

// --- derived ---------------------------------------------------------------
const library = computed<ClauseLibrary>(() =>
  activeTab.value === 'gaia' ? 'gaia' : 'organisation'
);
const isManaged = computed(() => library.value === 'gaia');

const canWrite = computed(() => canWriteClauses(library.value, authStore.userPermissions));
/** Taking a copy of a GAIA clause writes to the *organisation* library. */
const canCopyToOrganisation = computed(
  () => isManaged.value && canWriteClauses('organisation', authStore.userPermissions)
);

const addLabel = computed(() => (isManaged.value ? 'Add Gaia clause' : 'Add clause'));

const hasQuery = computed(() => searchQuery.value.trim().length > 0);
const hasFilters = computed(() => Object.keys(columnFilters.value).length > 0);
const narrowed = computed(() => hasQuery.value || hasFilters.value);

const countLabel = computed(() => {
  if (loading.value) return 'Loading';
  return `${totalCount.value} ${totalCount.value === 1 ? 'clause' : 'clauses'}`;
});

const tabItems = computed<GaiaTabBarItem[]>(() => [
  {
    key: 'organisation',
    label: 'Organisation clauses',
    count: libraryCounts.organisation ?? undefined,
  },
  { key: 'gaia', label: 'Gaia managed', count: libraryCounts.gaia ?? undefined },
]);

const showEmpty = computed(
  () => !loading.value && !loadError.value && clauses.value.length === 0 && !narrowed.value
);
const showNoMatch = computed(
  () => !loading.value && !loadError.value && clauses.value.length === 0 && narrowed.value
);
const showRows = computed(() => !loading.value && clauses.value.length > 0);

const emptyTitle = computed(() =>
  isManaged.value ? 'The managed library is empty' : 'No organisation clauses yet'
);
const emptyBody = computed(() =>
  isManaged.value
    ? 'Gaia clauses sync from the managed library. Add one manually if you need it before the next sync.'
    : 'Save the wording your team actually agrees to. Organisation clauses show up as fallbacks in reviews and can be inserted straight into templates.'
);

const skeletonCells = ['58%', { width: '70%', shape: 'chip' as const }, '84%', '70%', '60%'];

const tableColumns: GaiaDataColumn[] = [
  { key: 'name', label: 'Clause', width: 'minmax(300px,2.4fr)', sortable: true },
  { key: 'contract_types', label: 'Contract types', width: '180px', sortable: true },
  { key: 'clause_category', label: 'Category', width: '150px', sortable: true },
  { key: 'jurisdiction', label: 'Jurisdiction', width: '120px', sortable: true },
  { key: 'language', label: 'Language', width: '96px', sortable: true },
];

const rowMenuOptions = computed(() => {
  const options: Record<string, unknown>[] = [{ key: 'copy', label: 'Copy clause text' }];
  if (canCopyToOrganisation.value) {
    options.push({ key: 'copy-to-org', label: 'Copy to organisation' });
  }
  if (canWrite.value) {
    options.push(
      { key: 'edit', label: 'Edit clause' },
      { key: 'divider', type: 'divider' },
      { key: 'delete', label: 'Delete clause', props: gaiaDangerOption }
    );
  }
  return options;
});

// --- data ------------------------------------------------------------------
/**
 * Which fetch is the current one. A superseded response must not land: it would
 * either paint the previous library's rows or, through `finally`, clear the
 * spinner while the request the user is actually waiting on is still in flight.
 */
let requestSeq = 0;

async function fetchClauses(): Promise<void> {
  const target = library.value;
  const seq = ++requestSeq;
  loading.value = true;
  loadError.value = null;
  try {
    const page = await ClauseLibraryService.search({
      library: target,
      text: searchQuery.value,
      page: currentPage.value,
      pageSize: pageSize.value,
      sortKey: sortKey.value,
      sortDirection: sortDirection.value,
      columnFilters: buildColumnFilters(columnFilters.value),
    });
    // A tab switched, or a newer query was issued, while this was in flight:
    // these rows answer a question nobody is asking any more.
    if (seq !== requestSeq || library.value !== target) return;
    clauses.value = page.results;
    totalCount.value = page.result_count;
    filterOptions.value = normalizeFilterOptions(page.filter_options);
    // Only the unnarrowed total is the library's size; a filtered count would
    // make the tab pill disagree with the tab it labels.
    if (!narrowed.value) libraryCounts[target] = page.result_count;
    staleSince.value = null;
  } catch (error) {
    logger.error('Error fetching clauses:', error);
    if (seq !== requestSeq || library.value !== target) return;
    // Rows already on screen survive the failure behind a stale banner; only a
    // cold failure takes over the card.
    if (clauses.value.length > 0) {
      staleSince.value = new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else {
      loadError.value = 'Couldn’t load clauses';
    }
  } finally {
    if (seq === requestSeq) loading.value = false;
  }
}

/** The count for the tab the user is *not* on, so its pill is honest too. */
async function fetchOtherCount(): Promise<void> {
  const other: ClauseLibrary = isManaged.value ? 'organisation' : 'gaia';
  try {
    libraryCounts[other] = await ClauseLibraryService.count(other);
  } catch (error) {
    // A missing pill is not worth a toast — the tab still works.
    logger.error('Error counting clauses:', error);
  }
}

async function refresh(): Promise<void> {
  await Promise.all([fetchClauses(), fetchOtherCount()]);
}

/**
 * One fetch per turn of the loop.
 *
 * Several of the query refs move together — switching tab resets the search,
 * the filters and the page in one go, and a filter change resets the page —
 * and each has a watcher. Coalescing to the next tick is what keeps that one
 * request instead of four. Paths that need to *await* the new rows (a delete,
 * a save) call `fetchClauses`/`refresh` directly.
 */
let fetchScheduled = false;
function requestFetch(): void {
  if (fetchScheduled) return;
  fetchScheduled = true;
  void nextTick(() => {
    fetchScheduled = false;
    void fetchClauses();
  });
}

/** Any change that changes *which* clauses match starts again from page 1. */
function reloadFromFirstPage(): void {
  currentPage.value = 1;
  requestFetch();
}

function applySearch(): void {
  sortActivated.value = false;
  reloadFromFirstPage();
}

/**
 * A new page size re-slices the result set, so the list goes back to page 1 —
 * staying on page 5 at 50 per page asks for an offset past the last clause and
 * comes back empty. `GaiaPagination` reports the size and leaves this to the
 * caller.
 */
function setPageSize(size: number): void {
  pageSize.value = size;
  currentPage.value = 1;
}

watch([currentPage, pageSize], () => {
  if (currentPage.value > 0 && pageSize.value > 0) requestFetch();
});

watch(columnFilters, reloadFromFirstPage);

onMounted(() => {
  void refresh();
});

// --- toolbar ---------------------------------------------------------------
/** A tab switch is a different library: nothing narrowing it carries over. */
function selectTab(key: string): void {
  if (key === activeTab.value) return;
  drawerOpen.value = false;
  selected.value = null;
  rowDelete.cancel();
  // The other library's rows go now, not when its page arrives: if that fetch
  // fails, the stale banner would otherwise present them as this tab's.
  clauses.value = [];
  totalCount.value = 0;
  filterOptions.value = {};
  staleSince.value = null;
  searchQuery.value = '';
  sortActivated.value = false;
  setColumnFilters({});
  activeTab.value = key;
  currentPage.value = 1;
  requestFetch();
  void fetchOtherCount();
}

function clearNarrowing(): void {
  searchQuery.value = '';
  setColumnFilters({});
  reloadFromFirstPage();
}

function onSort(key: string): void {
  if (sortActivated.value && sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortActivated.value = true;
    sortKey.value = key;
    // A date opens newest-first; everything else here is text and opens A–Z.
    sortDirection.value = key === 'created_at' ? 'desc' : 'asc';
  }
  reloadFromFirstPage();
}

// --- formatting ------------------------------------------------------------
const emptyCell = '—';
/** How many contract-type tags a row shows before the rest collapse into "+N". */
const MAX_TYPE_TAGS = 2;

function visibleTypes(clause: LibraryClause): string[] {
  return contractTypeNames(clause.contract_types).slice(0, MAX_TYPE_TAGS);
}

function overflowTypes(clause: LibraryClause): number {
  return Math.max((clause.contract_types?.length ?? 0) - MAX_TYPE_TAGS, 0);
}

function categoryLabel(clause: LibraryClause): string {
  return clause.clause_category ? clauseCategoryMap[clause.clause_category] : emptyCell;
}

// --- row actions -----------------------------------------------------------
function openDetail(clause: LibraryClause): void {
  selected.value = clause;
  drawerOpen.value = true;
}

async function copyClauseText(clause: LibraryClause): Promise<void> {
  if (!(await copy(clause.content))) return;
  message.success(`${clause.name} copied.`);
}

function onRowMenu(key: string, clause: LibraryClause): void {
  if (key === 'copy') {
    void copyClauseText(clause);
    return;
  }
  if (key === 'copy-to-org') {
    openCopyToOrganisation(clause);
    return;
  }
  if (key === 'edit') {
    openEdit(clause);
    return;
  }
  rowDelete.arm(clause);
}

async function deleteClauseRow(clause: LibraryClause): Promise<void> {
  const target = library.value;
  await rowDelete.run(clause, {
    work: async () => {
      await ClauseLibraryService.remove(target, clause.uid);
      message.success(`${clause.name} deleted.`);
      if (selected.value?.uid === clause.uid) {
        drawerOpen.value = false;
        selected.value = null;
      }
      await refresh();
    },
    errorText: () => 'Couldn’t delete this clause. Nothing was changed.',
  });
}

/** The drawer only asks; the row strip is still where a delete is confirmed. */
function armDeleteFromDrawer(clause: LibraryClause): void {
  drawerOpen.value = false;
  rowDelete.arm(clause);
}

// --- create / edit ---------------------------------------------------------
function openCreate(): void {
  if (!canWrite.value) return;
  formMode.value = 'create';
  formLibrary.value = library.value;
  formClause.value = null;
  formPrefill.value = null;
  formHeading.value = undefined;
  formOpen.value = true;
}

function openEdit(clause: LibraryClause): void {
  if (!canWrite.value) return;
  formMode.value = 'edit';
  formLibrary.value = library.value;
  formClause.value = clause;
  formPrefill.value = null;
  formHeading.value = undefined;
  formOpen.value = true;
}

/**
 * Take a GAIA clause into the organisation's own library.
 *
 * It opens as an editable create rather than saving straight away: the copy
 * becomes the organisation's wording from that moment, and a copy nobody could
 * adjust first would be a worse feature than none.
 */
function openCopyToOrganisation(clause: LibraryClause): void {
  if (!canCopyToOrganisation.value) return;
  drawerOpen.value = false;
  formMode.value = 'create';
  formLibrary.value = 'organisation';
  formClause.value = null;
  formPrefill.value = clause;
  formHeading.value = 'Copy to organisation';
  formOpen.value = true;
}

async function onFormSaved(clause: LibraryClause): Promise<void> {
  // A copy landed in the other library, so show the user where it went.
  if (formLibrary.value !== library.value) {
    selectTab(formLibrary.value);
    return;
  }
  if (selected.value?.uid === clause.uid) selected.value = clause;
  await refresh();
}

// --- chrome ----------------------------------------------------------------
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const tileSurface = gaiaNeutral.surfaceTint;
const tileBorder = gaiaNeutral.borderMuted;
const tileInk = gaiaNeutral.textTertiary;
const managedSurface = gaiaAccent.surface;
const managedBorder = gaiaAccent.border;
const managedInk = gaiaAccent.base;
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.repository }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.clauseLibrary }}</n-breadcrumb-item>
    </template>
    <n-flex vertical :size="18">
      <!-- Title + what the two libraries are, actions pushed right. -->
      <n-flex align="flex-start" :size="[24, 12]">
        <n-flex vertical :size="6" :style="{ flex: '1 1 320px', minWidth: 0 }">
          <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
            Clause Library
          </gaia-header>
          <gaia-text size="body" tone="tertiary" :style="{ maxWidth: '64ch' }">
            Approved fallback wording, ready to drop into a review or a template. Gaia keeps the
            managed set current; your organisation clauses stay yours.
          </gaia-text>
        </n-flex>

        <n-flex align="center" justify="end" :size="8" :style="{ marginLeft: 'auto' }">
          <gaia-button size="medium" :disabled="loading" @click="refresh">
            <template #icon>
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M8 2.6a5.4 5.4 0 1 0 5.2 4" />
                <path d="M13.4 2.4v3.2h-3.2" />
              </svg>
            </template>
            Refresh
          </gaia-button>

          <!-- The GAIA library is maintained by Gaia, so writing to it is a
               platform-admin capability; the organisation's own is an org
               admin's. The button stays visible and says which, rather than
               vanishing and leaving the capability undiscoverable. -->
          <n-tooltip :disabled="canWrite" placement="bottom-end">
            <template #trigger>
              <span>
                <gaia-button type="primary" size="medium" :disabled="!canWrite" @click="openCreate">
                  <template #icon>
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    >
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </template>
                  {{ addLabel }}
                </gaia-button>
              </span>
            </template>
            {{
              isManaged
                ? 'The Gaia managed library is maintained by Gaia. Only platform admins can change it.'
                : 'Only organisation admins can add clauses. Ask an admin in your workspace.'
            }}
          </n-tooltip>
        </n-flex>
      </n-flex>

      <gaia-tab-bar :items="tabItems" :active="activeTab" @select="selectTab" />

      <n-flex align="center" :size="10">
        <gaia-search-input
          v-model:value="searchQuery"
          placeholder="Search name or clause text"
          :debounce="500"
          @search="applySearch"
        />
        <gaia-button
          size="small"
          :type="hasFilters ? 'primary' : 'default'"
          :quaternary="!hasFilters"
          @click="showFilterPanel = !showFilterPanel"
        >
          <template #icon>
            <svg
              width="13"
              height="13"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M2.5 4h11l-4.2 5v4l-2.6-1.4V9z" />
            </svg>
          </template>
          Filter
        </gaia-button>
        <gaia-count-label :style="{ marginLeft: 'auto' }">{{ countLabel }}</gaia-count-label>
      </n-flex>

      <gaia-filter-panel
        v-model:visible="showFilterPanel"
        :filter-config="filterConfig"
        :filter-options="filterOptions"
        :active-filters="columnFilters"
        :value-label-map="valueLabelMap"
        @update:filters="setColumnFilters"
      />

      <gaia-data-table
        :columns="tableColumns"
        :sort-key="sortKey"
        :sort-direction="sortDirection === 'asc' ? 'asc' : 'desc'"
        :sort-active="sortActivated"
        :action-width="68"
        @sort="onSort"
      >
        <template #banner>
          <gaia-banner v-if="staleSince" plain type="error">
            Couldn’t refresh — showing clauses as of {{ staleSince }}.
            <template #action>
              <gaia-button text size="tiny" @click="fetchClauses">Retry</gaia-button>
            </template>
          </gaia-banner>
        </template>

        <template #default="{ rowStyle }">
          <gaia-table-skeleton
            v-if="loading"
            :row-style="rowStyle"
            :cells="skeletonCells"
            :rows="7"
            :row-height="64"
          />

          <gaia-empty-state v-else-if="loadError" status="alert" :title="loadError">
            The server didn’t respond in time. Nothing was changed — retrying is safe.
            <template #actions>
              <gaia-button type="primary" size="small" @click="fetchClauses">Retry</gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="showEmpty" dashed :title="emptyTitle">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M4.5 2.5h6L14 6v9.5H4.5z" />
                <path d="M6.8 7.5h4.4M6.8 10.5h3" />
              </svg>
            </template>
            {{ emptyBody }}
            <template v-if="canWrite" #actions>
              <gaia-button type="primary" size="medium" @click="openCreate">
                {{ addLabel }}
              </gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="showNoMatch" title="No clause matches these filters">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              >
                <circle cx="8" cy="8" r="5" />
                <path d="M11.8 11.8 15.5 15.5" />
              </svg>
            </template>
            Try a different wording, or widen the category, contract type, jurisdiction and language
            filters.
            <template #actions>
              <gaia-button size="small" @click="clearNarrowing">Clear filters</gaia-button>
            </template>
          </gaia-empty-state>

          <template v-else-if="showRows">
            <template v-for="clause in clauses" :key="clause.uid">
              <!-- A row in trouble is replaced by its strip, never stacked with it. -->
              <gaia-table-row-state
                v-if="rowDelete.stateOf(clause.uid) === 'confirm'"
                variant="confirm"
                :title="`Delete “${clause.name}”?`"
                @cancel="rowDelete.cancel()"
                @confirm="deleteClauseRow(clause)"
              >
                The clause leaves the library for everyone. Playbooks that reference it keep their
                own copy of the wording.
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(clause.uid) === 'busy'"
                variant="busy"
                note="Undo not available"
              >
                Deleting “{{ clause.name }}”…
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(clause.uid) === 'error'"
                variant="error"
                @retry="deleteClauseRow(clause)"
                @dismiss="rowDelete.dismissError()"
              >
                {{ rowDelete.errorTextOf(clause.uid) }}
              </gaia-table-row-state>

              <div v-else class="row" :style="rowStyle" @click="openDetail(clause)">
                <div class="name-cell">
                  <div class="tile" :class="{ managed: isManaged }">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M4 2.5h6.5L13 5v8.5H4z" />
                      <path d="M6 6.5h4M6 9h2.5" />
                    </svg>
                  </div>
                  <div class="name-stack">
                    <n-flex align="center" :size="8" :wrap="false" :style="{ minWidth: 0 }">
                      <gaia-text truncate size="body" weight="semibold" tone="ink">
                        {{ clause.name }}
                      </gaia-text>
                      <gaia-tag v-if="isManaged" variant="accent" size="tiny">Gaia</gaia-tag>
                    </n-flex>
                    <!-- Two lines of the wording: enough to tell two similarly
                         named fallbacks apart without opening either. -->
                    <gaia-text size="micro" tone="subtle" class="preview">
                      {{ clause.content }}
                    </gaia-text>
                  </div>
                </div>

                <div class="cell types">
                  <gaia-tag v-for="type in visibleTypes(clause)" :key="type" size="tiny">
                    {{ type }}
                  </gaia-tag>
                  <gaia-text v-if="overflowTypes(clause)" size="micro" tone="muted">
                    +{{ overflowTypes(clause) }}
                  </gaia-text>
                  <gaia-text v-if="!clause.contract_types?.length" size="dense" tone="muted">
                    {{ emptyCell }}
                  </gaia-text>
                </div>

                <div class="cell">
                  <gaia-text truncate size="dense" tone="text">
                    {{ categoryLabel(clause) }}
                  </gaia-text>
                </div>

                <div class="cell">
                  <gaia-text truncate size="dense" tone="text">
                    {{ clause.jurisdiction || emptyCell }}
                  </gaia-text>
                </div>

                <div class="cell">
                  <gaia-text truncate size="dense" tone="subtle">
                    {{ clause.language || emptyCell }}
                  </gaia-text>
                </div>

                <n-flex justify="end" align="center" :size="2" :wrap="false">
                  <gaia-icon-button
                    :dimension="28"
                    quaternary
                    title="Copy clause text"
                    @click.stop="copyClauseText(clause)"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
                      <path d="M10.5 3.5H4a1.5 1.5 0 0 0-1.5 1.5v6" />
                    </svg>
                  </gaia-icon-button>
                  <gaia-dropdown
                    :options="rowMenuOptions"
                    placement="bottom-end"
                    @select="(key: string) => onRowMenu(key, clause)"
                  >
                    <gaia-icon-button :dimension="28" quaternary title="More" @click.stop>
                      ⋯
                    </gaia-icon-button>
                  </gaia-dropdown>
                </n-flex>
              </div>
            </template>
          </template>
        </template>

        <template v-if="showRows" #footer>
          <gaia-pagination
            :page="currentPage"
            :page-size="pageSize"
            :total="totalCount"
            :page-sizes="CLAUSE_PAGE_SIZES"
            @update:page="(page: number) => (currentPage = page)"
            @update:page-size="setPageSize"
          />
        </template>
      </gaia-data-table>
    </n-flex>

    <gaia-clause-detail-drawer
      v-model:show="drawerOpen"
      :clause="selected"
      :library="library"
      :can-write="canWrite"
      :can-copy-to-organisation="canCopyToOrganisation"
      @edit="openEdit"
      @delete="armDeleteFromDrawer"
      @copy-to-organisation="openCopyToOrganisation"
    />

    <gaia-clause-form-modal
      v-model:show="formOpen"
      :mode="formMode"
      :library="formLibrary"
      :clause="formClause"
      :prefill="formPrefill"
      :heading="formHeading"
      @saved="onFormSaved"
    />
  </Layout>
</template>

<style scoped>
/* A body row rides the grid template GaiaDataTable hands out through the slot,
   so only the row's own chrome lives here. Taller than the standard 56px row:
   the name cell carries a second line of clause text. */
.row {
  display: grid;
  align-items: center;
  gap: 16px;
  min-height: 64px;
  padding: 11px 18px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.types {
  flex-wrap: wrap;
  gap: 5px;
}

.name-cell {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  min-width: 0;
}

.name-stack {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

/* The glyph tile marks which library the row came from at a glance, next to the
   name rather than as another column. */
.tile {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-top: 1px;
  border: 1px solid v-bind(tileBorder);
  border-radius: 8px;
  background: v-bind(tileSurface);
  color: v-bind(tileInk);
}

.tile.managed {
  border-color: v-bind(managedBorder);
  background: v-bind(managedSurface);
  color: v-bind(managedInk);
}

/* Two lines then an ellipsis. `line-clamp` is the only way to cap a wrapped
   run of text by lines, and GaiaText's `truncate` is single-line. */
.preview {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  line-height: 1.55;
  text-wrap: pretty;
}
</style>
